import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Ticket,
    Clock,
    User,
    MapPin,
    ArrowRight,
    MessageSquare,
    Eye,
    TrendingUp,
    PlayCircle,
    UserPlus,
    CheckCircle,
    AlertTriangle,
    Hash,
    Search,
    CalendarCheck,
    History
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '../../lib/supabase';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useInfiniteTickets } from '../../hooks/useInfiniteTickets';

const StaffNewTickets = () => {
    const { tickets, claimTicket, assignNextTicket } = useTickets();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [claimingId, setClaimingId] = useState(null);

    // Auto-Assign State
    const [isAssigning, setIsAssigning] = useState(false);

    // Admin Override State
    const [showList, setShowList] = useState(false);
    const isAdmin = ['admin', 'head_staff', 'direcao', 'programador'].includes(user?.accessLevel);

    // Filtros e Busca persistentes
    const [filters, setFilters] = useState(() => {
        if (!user?.discordId) return {
            department: 'all',
            order: 'oldest',
            city: 'all',
            priority: 'all',
            status: 'all'
        };
        const saved = localStorage.getItem(`staff_pref_new_${user.discordId}`);
        return saved ? JSON.parse(saved) : {
            department: 'all',
            order: 'oldest',
            city: 'all',
            priority: 'all',
            status: 'all'
        };
    });

    const [searchTerm, setSearchTerm] = useState('');

    // Queue Flow Control State
    const [flowConfig, setFlowConfig] = useState(null);

    // Fetch Flow Config on mount
    useEffect(() => {
        const fetchFlowConfig = async () => {
            const { data } = await supabase.from('system_settings').select('value').eq('key', 'queue_flow_control').single();
            if (data && data.value) setFlowConfig(data.value);
        };
        fetchFlowConfig();
    }, []);

    // Calcula total global da fila instanciado do contexto
    const realtimeNewTicketsCount = React.useMemo(() => {
        return tickets?.filter(t =>
            t.status === 'aberto' &&
            !t.assignedTo &&
            t.type !== 'escalation' &&
            (!t.responses || !t.responses.some(r => r?.isStaff))
        ).length || 0;
    }, [tickets]);

    // Check restriction
    const isFlowRestricted = React.useMemo(() => {
        if (!flowConfig || !flowConfig.restrictedCities || flowConfig.restrictedCities.length === 0) return false;
        const threshold = parseInt(flowConfig.threshold, 10) || 20;
        return realtimeNewTicketsCount > threshold;
    }, [realtimeNewTicketsCount, flowConfig]);

    const {
        tickets: newTickets,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        lastTicketRef // Destructure missing ref
    } = useInfiniteTickets({
        queryBuilder: (query) => {
            // BASE LOGIC: Not assigned and Not closed
            query = query.is('assigned_to', null).not('status', 'in', '("fechado","resolvido","aguardando_sistema")');

            // Filters
            if (filters.department !== 'all') query = query.eq('category', filters.department);
            if (filters.city !== 'all') query = query.eq('city', filters.city);

            if (filters.priority !== 'all') {
                if (filters.priority === 'normal') {
                    query = query.or('priority.eq.normal,priority.eq.media,priority.eq.medium');
                } else if (filters.priority === 'alta') {
                    query = query.or('priority.eq.alta,priority.eq.high');
                } else if (filters.priority === 'baixa') {
                    query = query.or('priority.eq.baixa,priority.eq.low');
                } else if (filters.priority === 'urgente') {
                    query = query.or('priority.eq.urgente,priority.eq.urgent');
                } else {
                    query = query.eq('priority', filters.priority);
                }
            }

            if (filters.status !== 'all') query = query.eq('status', filters.status);

            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                query = query.or(`title.ilike.%${term}%,id.ilike.%${term}%,author_name.ilike.%${term}%,city.ilike.%${term}%`);
            }

            if (filters.order === 'newest') {
                query = query.order('created_at', { ascending: false });
            } else {
                query = query.order('created_at', { ascending: true });
            }

            // QUEUE FLOW CONTROL INTERCEPT
            if (isFlowRestricted && flowConfig?.restrictedCities?.length > 0) {
                // Must explicitly prevent selected cities from appearing
                flowConfig.restrictedCities.forEach(city => {
                    query = query.neq('city', city);
                });
            }

            return query;
        },
        pageSize: 20
    }, [filters, searchTerm, isFlowRestricted, flowConfig]);

    // Priority Sorting Helper (Client-Side for immediate effect)
    const getPriorityScore = (p) => {
        const priority = (p || '').toLowerCase();
        if (priority === 'urgente' || priority === 'urgent') return 4;
        if (priority === 'alta' || priority === 'high') return 3;
        if (priority === 'media' || priority === 'medium' || priority === 'normal') return 2;
        if (priority === 'baixa' || priority === 'low') return 1;
        return 0;
    };

    const sortedTickets = React.useMemo(() => {
        const sorted = [...newTickets];

        sorted.sort((a, b) => {
            const scoreA = getPriorityScore(a.priority);
            const scoreB = getPriorityScore(b.priority);

            if (scoreA !== scoreB) {
                return scoreB - scoreA;
            }

            const dateA = new Date(a.createdAt || a.created_at || 0);
            const dateB = new Date(b.createdAt || b.created_at || 0);

            if (filters.order === 'newest') {
                return dateB - dateA;
            } else {
                return dateA - dateB;
            }
        });

        return sorted;
    }, [newTickets, filters.order]);

    // Salvar preferências
    useEffect(() => {
        if (user?.discordId) {
            localStorage.setItem(`staff_pref_new_${user.discordId}`, JSON.stringify(filters));
        }
    }, [filters, user]);


    // Categorias disponíveis
    const categories = [
        { id: 'suporte_duvidas', label: 'Dúvidas Gerais', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
        { id: 'compras_doacoes', label: 'Compras & Doações', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
        { id: 'cupons_descontos', label: 'Cupons & Descontos', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
        { id: 'denuncia_jogador', label: 'Denúncia Jogador', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
        { id: 'contestar_punicao', label: 'Revogar Punição', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
        { id: 'programacao', label: 'Problemas Técnicos', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
        { id: 'denuncia_staff', label: 'Denúncia Staff', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30' }
    ];

    const getCategoryDetails = (categoryId) => {
        if (categoryId === 'ticket_automatico') return { label: 'Automático', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
        const category = categories.find(c => c.id === categoryId);
        return category || { label: categoryId, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            'aberto': 'bg-green-500/20 text-green-400 border-green-500/30',
            'aguardando_sistema': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            'fechado': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'aguardando_usuario': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'aguardando_staff': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            'em_andamento': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
            'escalado': 'bg-red-500/20 text-red-400 border-red-500/30',
            'resolvido': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'controle_qualidade': 'bg-blue-500/20 text-blue-400 border-blue-500/30'
        };
        return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'aberto': 'Aberto',
            'aguardando_sistema': 'Verificando',
            'fechado': 'Resolvido',
            'aguardando_usuario': 'Aguard. Você',
            'aguardando_staff': 'Aguard. Staff',
            'em_andamento': 'Em Andamento',
            'escalado': 'Escalado',
            'resolvido': 'Resolvido',
            'controle_qualidade': 'C. Qualidade'
        };
        return labels[status] || status.replace(/_/g, ' ');
    };

    const getPriorityBadgeColor = (priority) => {
        const p = (priority || '').toLowerCase();
        if (p === 'alta' || p === 'high' || p === 'urgent' || p === 'urgente') return 'bg-red-500/20 text-red-400 border-red-500/30';
        if (p === 'media' || p === 'medium' || p === 'normal') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        if (p === 'baixa' || p === 'low') return 'bg-green-500/20 text-green-400 border-green-500/30';
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    };

    const formatPriorityLabel = (priority) => {
        const p = (priority || '').toLowerCase();
        if (p === 'alta' || p === 'high') return 'Alta';
        if (p === 'urgent' || p === 'urgente') return 'Urgente';
        if (p === 'media' || p === 'medium' || p === 'normal') return 'Normal';
        if (p === 'baixa' || p === 'low') return 'Baixa';
        return priority || 'N/A';
    };

    // Gera uma cor consistente baseada no nome da cidade
    const getCityColor = (cityName) => {
        if (!cityName) return 'bg-gray-900/50 text-gray-400 border-gray-700';
        const colors = [
            'bg-pink-500/10 text-pink-400 border-pink-500/20',
            'bg-violet-500/10 text-violet-400 border-violet-500/20',
            'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
            'bg-amber-500/10 text-amber-400 border-amber-500/20',
            'bg-lime-500/10 text-lime-400 border-lime-500/20',
            'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20',
            'bg-sky-500/10 text-sky-400 border-sky-500/20',
        ];
        let hash = 0;
        for (let i = 0; i < cityName.length; i++) {
            hash = cityName.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const formatRelativeTime = (dateString, addSuffix = true) => {
        if (!dateString) return 'Data desconhecida';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Data inválida';

        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Agora';
        if (diffInMinutes < 60) return `${diffInMinutes}min${addSuffix ? ' atrás' : ''}`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h${addSuffix ? ' atrás' : ''}`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d${addSuffix ? ' atrás' : ''}`;

        const diffInWeeks = Math.floor(diffInDays / 7);
        return `${diffInWeeks}sem${addSuffix ? ' atrás' : ''}`;
    };

    const handleQuickClaim = async (e, ticketId) => {
        e.stopPropagation(); // Impede a abertura do ticket
        setClaimingId(ticketId);
        try {
            const result = await claimTicket(ticketId);
            if (result.success) {
                toast.success('Ticket assumido com sucesso!');
                navigate(`/staff/tickets/${ticketId}`);
            } else {
                toast.error('Erro ao assumir ticket: ' + result.error);
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro ao processar solicitação.');
        } finally {
            setClaimingId(null);
        }
    };

    // --- AUTO-ASSIGN HANDLER ---
    const handleAutoAssign = async () => {
        if (loading) return;
        setIsAssigning(true);
        // Pequeno delay para UX
        await new Promise(r => setTimeout(r, 600));

        try {
            // Pass current filters to respect department/city preferences if set
            // BUT force status='aberto' inside context and order='oldest'
            const result = await assignNextTicket(filters);

            if (result.success) {
                toast.success('Ticket atribuído! Redirecionando...');
                navigate(`/staff/tickets/${result.ticketId}`);
            } else {
                toast.error(result.error || 'Não há tickets disponíveis para você no momento.');
                // Refresh para garantir
                if (loadMore) loadMore(); // Or refresh if available
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro interno. Tente novamente.');
        } finally {
            setIsAssigning(false);
        }
    };

    // Logic moved to hook

    // Lista de Cidades (Fixa)
    const availableCities = ['CAPITAL', 'VALLEY NORTE', 'MORADA DO VALLEY', 'VALLEY SUL'];

    // Componente de Filtro
    const FilterDropdown = ({ label, value, onChange, options, icon: Icon }) => (
        <div className="flex flex-col gap-1.5 sm:gap-2 flex-1 min-w-0">
            <label className="text-[10px] uppercase font-bold text-gray-500 tracking-widest flex items-center gap-1.5 pl-1">
                {Icon && <Icon className="w-3 h-3 text-blue-400/70" />}
                {label}
            </label>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-gray-200 h-10 sm:h-11 rounded-xl px-3 py-2 text-sm focus:outline-none focus:bg-white/10 focus:border-blue-500/30 transition-all appearance-none cursor-pointer hover:bg-white/10 hover:border-blue-500/30 focus:ring-0"
                >
                    {options.map(opt => (
                        <option
                            key={opt.value}
                            value={opt.value}
                            className="bg-gray-900 text-gray-200 py-2"
                        >
                            {opt.label}
                        </option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4 fill-current opacity-50" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
                </div>
            </div>
        </div>
    );

    const TicketCard = React.forwardRef(({ ticket }, ref) => {
        const categoryDetails = getCategoryDetails(ticket.category);
        const isClaiming = claimingId === ticket.id;
        const displayContent = ticket.description || (ticket.responses?.[0]?.content) || 'Sem descrição informada.';
        const responseCount = ticket.responses ? ticket.responses.length : 0;
        const isUnassigned = !ticket.assignedTo;

        const assignee = ticket.assignees && ticket.assignees.length > 0
            ? ticket.assignees[ticket.assignees.length - 1]
            : null;

        const assigneeName = assignee?.name || assignee?.codename || ticket.assignedName || 'Staff';
        const assigneeDiscordId = assignee?.discordId;
        const assigneeAvatar = assignee?.avatar || ticket.assignedAvatar;

        const avatarUrl = assigneeDiscordId && assigneeAvatar && !assigneeAvatar.startsWith('http')
            ? `https://cdn.discordapp.com/avatars/${assigneeDiscordId}/${assigneeAvatar}.png`
            : assigneeAvatar;

        const isAssignedToMe = ticket.assignedTo === user?.id;


        return (
            <motion.div
                ref={ref}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]`}
                onClick={() => navigate(`/staff/tickets/${ticket.id}`)}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative p-5">
                    <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-4 mb-4">
                        {/* LADO ESQUERDO */}
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex flex-col mr-2">
                                <span className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-0.5">Ticket ID</span>
                                <span className="text-white font-mono text-sm font-bold">#{ticket.id}</span>
                            </div>
                            <div className="h-8 w-[1px] bg-white/10 mx-1 hidden sm:block" />

                            <Badge className={`${categoryDetails.color} border-none px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-md`}>
                                {categoryDetails.label}
                            </Badge>

                            {ticket.city && (
                                <Badge variant="outline" className={`ml-1 ${getCityColor(ticket.city)} px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md flex items-center gap-1 shadow-sm`}>
                                    <MapPin className="w-3 h-3" />
                                    {ticket.city}
                                </Badge>
                            )}

                            {/* PRIORIDADE E STATUS */}
                            <div className={`flex items-center space-x-1.5 px-2 py-1 rounded-md border ${getPriorityBadgeColor(ticket.priority)}`}>
                                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${ticket.priority === 'alta' || ticket.priority === 'high' ? 'bg-red-400' : ticket.priority === 'media' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                                <span className="text-[10px] font-bold uppercase tracking-tight">{formatPriorityLabel(ticket.priority)}</span>
                            </div>

                            <Badge className={`text-[10px] px-3 py-1 font-bold uppercase tracking-wider ${getStatusBadgeColor(ticket.status)} border-none rounded-full shadow-lg shadow-black/20`}>
                                {getStatusLabel(ticket.status)}
                            </Badge>
                        </div>

                        {/* LADO DIREITO */}
                        <div className="flex flex-wrap items-center justify-end gap-3 flex-1 lg:flex-none">
                            <div className="flex items-center gap-3 text-[10px] text-gray-500">
                                <span className="flex items-center" title="Criado em">
                                    <CalendarCheck className="h-3 w-3 mr-1 opacity-50" />
                                    {formatRelativeTime(ticket.createdAt || ticket.created_at)}
                                </span>

                                {(ticket.lastUpdate || ticket.last_update || ticket.createdAt || ticket.created_at) && (
                                    <span className="flex items-center text-blue-400/80" title="Última atualização">
                                        <History className="h-3 w-3 mr-1 opacity-70" />
                                        {formatRelativeTime(ticket.lastUpdate || ticket.last_update || ticket.createdAt || ticket.created_at, false)}
                                    </span>
                                )}

                                <span className="flex items-center" title="Mensagens">
                                    <MessageSquare className="h-3 w-3 mr-1 opacity-50" />
                                    {responseCount}
                                </span>
                            </div>

                            <div className="pl-2 border-l border-white/10">
                                {isUnassigned ? (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-[10px] uppercase font-bold tracking-wider text-green-400 hover:text-green-300 hover:bg-green-500/10 transition-colors border border-green-500/20 rounded-lg group/btn"
                                        onClick={(e) => handleQuickClaim(e, ticket.id)}
                                        disabled={isClaiming}
                                    >
                                        {isClaiming ? (
                                            <span className="animate-pulse">...</span>
                                        ) : (
                                            <>
                                                <UserPlus className="w-3 h-3 mr-1 group-hover/btn:scale-110 transition-transform" />
                                                Assumir
                                            </>
                                        )}
                                    </Button>
                                ) : (() => {
                                    if (isAssignedToMe) {
                                        return (
                                            <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full pl-1 pr-2 py-0.5">
                                                <Avatar className="h-5 w-5 border border-blue-500/30">
                                                    <AvatarImage src={user?.avatar ? `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png` : undefined} />
                                                    <AvatarFallback className="bg-blue-900 text-blue-200 text-[8px]">{user?.name?.substring(0, 2).toUpperCase()}</AvatarFallback>
                                                </Avatar>
                                                <span className="text-[9px] font-bold uppercase tracking-wider text-blue-400">VOCÊ</span>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="flex items-center gap-2 bg-gray-800/50 border border-gray-700/50 rounded-full pl-1 pr-2 py-0.5">
                                            <Avatar className="h-5 w-5 border border-gray-600">
                                                <AvatarImage src={avatarUrl} />
                                                <AvatarFallback className="bg-gray-700 text-gray-300 text-[8px]">
                                                    {assigneeName.substring(0, 2).toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-[9px] font-medium text-gray-300 max-w-[80px] truncate">
                                                {assigneeName}
                                            </span>
                                        </div>
                                    );
                                })()}
                            </div>

                            <div className="flex items-center text-gray-400 group-hover:text-white transition-colors cursor-pointer">
                                <Eye className="h-4 w-4 transform group-hover:scale-110 transition-transform" />
                            </div>
                        </div>
                    </div>

                    <div className="mb-1">
                        <div className="flex justify-between items-start">
                            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors duration-300 line-clamp-1 mb-1 flex-1 mr-4">
                                {ticket.title || 'Solicitação sem título'}
                            </h3>
                        </div>
                        <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
                            {displayContent}
                        </p>
                    </div>
                </div>

                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-600" />
                <div className="absolute left-0 top-0 bottom-0 w-4 bg-blue-500/10 blur-md" />
            </motion.div>
        );
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-6">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white">Novos Chamados</h1>
                        <p className="text-gray-400 mt-1 text-sm sm:text-base">Tickets aguardando atendimento inicial</p>
                    </div>
                    {/* Admin Toggle */}
                    {isAdmin && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowList(!showList)}
                            className={`text-xs uppercase tracking-widest font-bold border ${showList ? 'border-red-500/30 text-red-400 bg-red-500/10' : 'border-blue-500/30 text-blue-400 bg-blue-500/10'}`}
                        >
                            {showList ? 'Ocultar Lista' : 'Ver Lista (Admin)'}
                        </Button>
                    )}
                </div>

                {/* PAINEL DE CONTROLE DE FILTROS E BUSCA */}
                <div className="relative group rounded-3xl p-[1px] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative bg-black/40 backdrop-blur-xl rounded-3xl p-4 sm:p-5 md:p-6 space-y-4 sm:space-y-6">

                        {/* VIEW 1: AUTO-ASSIGN (DEFAULT) */}
                        {!showList && (
                            <div className="flex flex-col items-center justify-center py-10 space-y-8 animate-in fade-in zoom-in duration-500">
                                <div className="text-center space-y-2">
                                    <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                                            {loading ? '...' : realtimeNewTicketsCount}
                                        </span> Tickets na Fila
                                    </h2>
                                    <p className="text-gray-400 text-lg">Clique abaixo para iniciar o próximo atendimento disponível</p>
                                </div>

                                <button
                                    onClick={handleAutoAssign}
                                    disabled={loading || isAssigning || realtimeNewTicketsCount === 0}
                                    className="group relative w-64 h-64 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                                >
                                    {/* Círculos animados de fundo */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full opacity-20 group-hover:opacity-30 blur-2xl transition-opacity animate-pulse"></div>
                                    <div className="absolute inset-0 rounded-full border border-white/10 group-hover:border-blue-500/30 transition-colors"></div>

                                    {/* Conteúdo do Botão */}
                                    <div className="absolute inset-4 bg-black/80 rounded-full border-2 border-white/10 group-hover:border-blue-500/50 shadow-2xl flex items-center justify-center backdrop-blur-md z-10">
                                        <div className="flex flex-col items-center gap-4 text-center p-4">
                                            {isAssigning ? (
                                                <>
                                                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                    <span className="text-blue-400 font-bold tracking-widest uppercase text-sm animate-pulse">Buscando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Ticket className="w-16 h-16 text-white group-hover:text-blue-400 transition-colors" />
                                                    <div className="space-y-1">
                                                        <span className="block text-xl font-bold text-white group-hover:text-blue-200 uppercase tracking-wider">Estou Disponível</span>
                                                        <span className="block text-xs text-gray-500 font-mono">ATENDER PRÓXIMO</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Anel de progresso decorativo (SVG) */}
                                    <svg className="absolute inset-0 w-full h-full rotate-[-90deg] pointer-events-none" viewBox="0 0 100 100">
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="48"
                                            fill="none"
                                            stroke="url(#gradient)"
                                            strokeWidth="0.5"
                                            className="opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out"
                                        />
                                        <defs>
                                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" stopColor="#60a5fa" />
                                                <stop offset="100%" stopColor="#c084fc" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </button>

                                <div className="text-xs text-gray-600 font-mono border border-white/5 rounded-full px-4 py-1">
                                    Status: {isAssigning ? 'Atribuindo...' : 'Aguardando Staff'}
                                </div>
                            </div>
                        )}

                        {/* VIEW 2: FILTERS & LIST (CONDITIONAL) */}
                        {showList && (
                            <div className="space-y-6 animate-in slide-in-from-top-4 duration-300">
                                {/* Barra de Busca Principal */}
                                <div className="relative z-10">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Search className="h-5 w-5 text-blue-400/80" />
                                    </div>
                                    <Input
                                        type="text"
                                        placeholder="Buscar por ID, Personagem, Discord, Cidade..."
                                        className="pl-10 sm:pl-12 h-12 sm:h-14 bg-white/5 border-white/10 text-white focus:border-blue-500/50 focus:bg-white/10 focus:ring-4 focus:ring-blue-500/10 transition-all rounded-xl text-sm sm:text-base shadow-lg shadow-black/20"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors group/clear"
                                        >
                                            <div className="bg-white/10 group-hover/clear:bg-white/20 rounded-full p-1.5 transition-colors">
                                                <ArrowRight className="h-3 w-3 rotate-45" />
                                            </div>
                                        </button>
                                    )}
                                </div>

                                {/* Grid de Filtros */}
                                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2 sm:gap-4">
                                    <FilterDropdown
                                        label="Departamento"
                                        value={filters.department}
                                        onChange={(val) => setFilters(prev => ({ ...prev, department: val }))}
                                        options={[
                                            { value: 'all', label: 'Todos' },
                                            ...categories.map(c => ({ value: c.id, label: c.label }))
                                        ]}
                                        icon={Hash}
                                    />
                                    <select
                                        value={filters.status}
                                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                        className="bg-gray-900/50 border border-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm w-[180px] focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    >
                                        <option value="all">Todos os Status</option>
                                        <option value="aberto">Abertos</option>
                                        <option value="em_andamento">Em Andamento</option>
                                        <option value="pendente_user">Pendente User</option>
                                        <option value="resolvido">Resolvidos</option>
                                        <option value="fechado">Fechados</option>
                                    </select>
                                    <FilterDropdown
                                        label="Prioridade"
                                        value={filters.priority}
                                        onChange={(val) => setFilters(prev => ({ ...prev, priority: val }))}
                                        options={[
                                            { value: 'all', label: 'Todas' },
                                            { value: 'alta', label: 'Alta' },
                                            { value: 'urgente', label: 'Urgente' },
                                            { value: 'normal', label: 'Normal' },
                                            { value: 'baixa', label: 'Baixa' }
                                        ]}
                                        icon={TrendingUp}
                                    />

                                    <FilterDropdown
                                        label="Ordem"
                                        value={filters.order}
                                        onChange={(val) => setFilters(prev => ({ ...prev, order: val }))}
                                        options={[
                                            { value: 'oldest', label: 'Mais Antigos' },
                                            { value: 'newest', label: 'Mais Recentes' }
                                        ]}
                                        icon={Clock}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* LISTA DE TICKETS (CONDITIONAL) */}
            {showList && (
                <div className="grid gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {sortedTickets.length > 0 ? (
                        <>
                            {sortedTickets.map((ticket, i) => (
                                <TicketCard
                                    key={ticket.id}
                                    ticket={ticket}
                                    ref={i === sortedTickets.length - 1 ? lastTicketRef : null}
                                />
                            ))}

                            {hasMore && (
                                <div className="flex justify-center pt-4 pb-8">
                                    <Button
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        variant="outline"
                                        className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                                    >
                                        {loadingMore ? 'Carregando...' : 'Carregar Mais'}
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        !loading && (
                            <div className="text-center py-12 bg-gray-900/20 rounded-2xl border border-gray-800">
                                <Ticket className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                                <h3 className="text-xl font-medium text-white">Tudo limpo!</h3>
                                <p className="text-gray-400">Não há novos chamados correspondentes aos filtros.</p>
                                {(searchTerm || filters.department !== 'all' || filters.city !== 'all') && (
                                    <Button
                                        variant="outline"
                                        className="mt-4 border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 hover:text-white"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setFilters({
                                                department: 'all',
                                                order: 'oldest',
                                                city: 'all',
                                                priority: 'all',
                                                status: 'all'
                                            });
                                        }}
                                    >
                                        Limpar Filtros
                                    </Button>
                                )}
                            </div>
                        )
                    )}
                    {loading && sortedTickets.length === 0 && (
                        <div className="text-center py-20 text-gray-400">Carregando novos tickets...</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StaffNewTickets;
